import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Clermont-l\'Hérault' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Clermont-l\'Hérault',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Clermont-l\'Hérault' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Clermont-l\'Hérault. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous sur les menuiseries anciennes du centre de Clermont-l\'Hérault ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nos techniciens interviennent sur les fenêtres bois et menuiseries anciennes du centre historique. Nous adaptons nos réparations aux dimensions atypiques et aux matériaux traditionnels.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quel est votre délai d\'intervention à Clermont-l\'Hérault ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Notre délai standard est de 24 à 48h, 7j/7. Nous couvrons Clermont-l\'Hérault et les communes alentour : Canet, Nébian, Paulhan. Pour une urgence, appelez le 09 80 80 22 42.',
      },
    },
  ],
};

export default function ClermontLHeraultPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Clermont | DFPRO"
        description="Baie coulissante, fenêtre ancienne ou volet roulant en panne à Clermont-l'Hérault ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-clermont-l-herault/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Clermont-l'Hérault"
        subtitle="Fenêtres anciennes du centre, baies coulissantes, volets roulants : nos techniciens réparent tous types de menuiseries à Clermont-l'Hérault. Devis gratuit, 24-48h."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Menuiserie en panne à Clermont-l'Hérault ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent à Clermont-l'Hérault et alentours, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          À Clermont-l'Hérault, nous intervenons sur les menuiseries anciennes du centre comme sur les baies coulissantes des résidences récentes. Crémone grippée, volet bloqué, double vitrage à changer : appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/clermont-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Clermont-l'Hérault"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante qui coince ou déraille</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Une baie qui <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince</Link> doit être réparée avant que le vantail ne sorte de son rail. Roulettes usées, rail encrassé, désalignement : nous diagnostiquons et réparons sur place.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Fenêtre ancienne et volet : réparation adaptée</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Le centre ancien de Clermont compte de nombreuses <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">fenêtres en bois</Link> aux dimensions hors standard. <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Crémone</Link> à l'ancienne, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link> fatigués : nous réparons avec soin.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Pour les <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link> : <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">déblocage</Link>, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link>, <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link>.
            </p>
            <Link to="/reparation-fenetre-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage fenêtre <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/clermont-volet-02.jpg"
              alt="Réparation de fenêtre à Clermont-l'Hérault"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/clermont-vitrage-03.webp"
              alt="Remplacement de vitrage à Clermont-l'Hérault"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et double vitrage</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> pour <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>, pose de <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link> sur mesure, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link>.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Stores : <Link to="/reparation-store-banne-herault/" className="text-accent-500 font-semibold hover:underline">bannes</Link>, <Link to="/reparation-bso-herault/" className="text-accent-500 font-semibold hover:underline">BSO</Link>, <Link to="/motorisation-store-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link>. Diagnostic gratuit.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Clermont-l'Hérault sous 24 à 48h</h2>
        <p>
          Nous couvrons Clermont-l'Hérault et ses environs : Canet, Nébian, Paulhan, Aspiran. Consultez aussi <Link to="/depannage-menuiserie-pezenas/" className="text-accent-500 font-semibold hover:underline">Pézenas</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}